// Stable string -> colour mapping for the Miller-Mize views, so the same
// entity reads as the same colour everywhere. Two consumers today: the
// shipping calendar keys on destination city ("fill the Nashville truck" at a
// glance) and the casting schedule keys on projectId, because multiple jobs
// share a platform and Creede: "colour is helpful when viewing this, just off
// an initial glance." Small fixed set of MUI palette shades; a simple string
// hash keeps the assignment stable across renders and sessions.
//
// KNOWN LIMITATION: the palette is modulo-10, so two entities can collide.
// Acceptable for a glance-level cue — the job number stays on the chip as the
// authoritative label.

export const NO_ENTITY_COLOR = "#757575"; // grey.600

// Every entry clears WCAG AA (4.5:1) against the ink getEntityTextColor
// picks for it; re-check the ratios if a shade changes.
//
// Widened from 10 to 16 on 2026-08-06, to raise the ceiling on how many
// entities getEntityColorByIndex can keep distinct.
const ENTITY_PALETTE = [
  "#1976d2", // blue 700
  "#9c27b0", // purple 500
  "#2e7d32", // green 800
  "#ed6c02", // orange 800
  "#d32f2f", // red 700
  "#0288d1", // lightBlue 700
  "#7b1fa2", // purple 700
  "#00796b", // teal 700
  "#c2185b", // pink 700
  "#5d4037", // brown 700
  "#455a64", // blueGrey 700
  "#4527a0", // deepPurple 800
  "#00838f", // cyan 800
  "#827717", // lime 900
  "#bf360c", // deepOrange 900
  "#283593", // indigo 800
];

const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

// Stable key -> colour. Use this when the caller CANNOT enumerate the entities
// it is colouring up front (the shipping calendar colours by destination city
// across an open-ended set), and accept that collisions happen.
export const getEntityColor = (key: string | null): string => {
  if (!key) return NO_ENTITY_COLOR;
  return ENTITY_PALETTE[hashString(key.toLowerCase()) % ENTITY_PALETTE.length];
};

// Rank -> colour, distinct for the first ENTITY_COLOR_COUNT ranks.
//
// Prefer this whenever the caller CAN enumerate what it is colouring, because
// a hash cannot deliver "tell these apart at a glance": with 16 swatches and
// the eight concurrent jobs Miller-Mize sometimes runs, ~85% of weeks would
// show at least one collision (measured, not estimated — 20k trials). Ranking
// the entities actually on screen makes them distinct by construction, at the
// cost of a colour that shifts when the visible set changes.
export const ENTITY_COLOR_COUNT = ENTITY_PALETTE.length;

export const getEntityColorByIndex = (index: number): string =>
  ENTITY_PALETTE[
    ((index % ENTITY_PALETTE.length) + ENTITY_PALETTE.length) %
      ENTITY_PALETTE.length
  ];

// A colour for each job in `ids`: the one it HOLDS, or else the next palette
// entry that no job in the list holds.
//
// The fallback used to be the palette entry at the job's rank, which is blind
// to what the others hold: a job third in the list fell back to green while
// the job beside it held green, and the two read as one. It matters most for a
// finished job, which has handed its colour back and is shown by fallback from
// then on. With nothing held this is the rank order exactly, so a set of
// uncoloured jobs looks as it always did.
export const colorsForJobs = (
  ids: string[],
  heldColorOf: (id: string) => string | null | undefined
): Map<string, string> => {
  const held = new Set<string>();
  for (const id of ids) {
    const own = heldColorOf(id);
    if (own) held.add(own);
  }
  const free = ENTITY_PALETTE.filter((color) => !held.has(color));
  // Every shade held: sharing beats blanking, as in ensureScheduleColor.
  const pool = free.length > 0 ? free : ENTITY_PALETTE;

  const colors = new Map<string, string>();
  let next = 0;
  for (const id of ids) {
    const own = heldColorOf(id);
    if (own) {
      colors.set(id, own);
    } else {
      colors.set(id, pool[next % pool.length]);
      next += 1;
    }
  }
  return colors;
};

// The palette above is fixed in both themes, so chip ink is derived from the
// swatch itself rather than a theme token, which would invert in dark mode.
const DARK_INK = "rgba(0,0,0,0.87)"; // MUI text.primary ink
const DARK_INK_ALPHA = 0.87;
const LIGHT_INK = "#fff";

// WCAG 2.1 relative luminance.
const channelLuminance = (channel: number): number => {
  const srgb = channel / 255;
  return srgb <= 0.03928 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
};

const relativeLuminance = (r: number, g: number, b: number): number =>
  0.2126 * channelLuminance(r) +
  0.7152 * channelLuminance(g) +
  0.0722 * channelLuminance(b);

export const getEntityTextColor = (background: string): string => {
  const r = parseInt(background.slice(1, 3), 16);
  const g = parseInt(background.slice(3, 5), 16);
  const b = parseInt(background.slice(5, 7), 16);
  // Anything but #rrggbb (a theme token, a short hex) parses to NaN, which
  // would silently pick white ink — unreadable on a light swatch.
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return DARK_INK;
  const backgroundLuminance = relativeLuminance(r, g, b);
  // DARK_INK is translucent, so it composites over the chip color.
  const inkLuminance = relativeLuminance(
    r * (1 - DARK_INK_ALPHA),
    g * (1 - DARK_INK_ALPHA),
    b * (1 - DARK_INK_ALPHA)
  );
  const contrastWithLightInk = 1.05 / (backgroundLuminance + 0.05);
  const contrastWithDarkInk =
    (backgroundLuminance + 0.05) / (inkLuminance + 0.05);
  return contrastWithDarkInk > contrastWithLightInk ? DARK_INK : LIGHT_INK;
};
